import React, { useState, useEffect } from 'react';
import { Task } from '../types/Task';
import TaskCard from './TaskCard';
import { Eye, EyeOff, ChevronLeft, ChevronRight, Edit2, Plus, Trash2, Check, X, Palette, GripVertical } from 'lucide-react';
import { columnApi, KanbanColumn } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import './KanbanBoard.css';

interface KanbanBoardProps {
  tasks: Task[];
  onEdit: (task: Task) => void;
  onDelete: (id: string) => void;
  onStatusChange: (taskId: string, newStatus: string) => void;
  onImagePreview?: (task: Task) => void;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ 
  tasks, 
  onEdit, 
  onDelete, 
  onStatusChange,
  onImagePreview
}) => {
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());
  const [isCompact, setIsCompact] = useState(false);
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [showAddColumn, setShowAddColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [newColumnColor, setNewColumnColor] = useState('#646669');
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);
  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);

  const { currentTheme } = useTheme();

  // Theme-specific color palettes
  const getThemeColors = () => {
    const colorPalettes = {
      'serika-dark': [
        '#e2b714', '#d4af37', '#f4d03f', '#f7dc6f', '#fcf3cf',
        '#ca4754', '#e74c3c', '#f1948a', '#fadbd8', '#fdeaea'
      ],
      'serika-light': [
        '#e2b714', '#d4af37', '#f4d03f', '#f7dc6f', '#fcf3cf',
        '#ca4754', '#e74c3c', '#f1948a', '#fadbd8', '#fdeaea'
      ],
      'dracula': [
        '#bd93f9', '#8be9fd', '#50fa7b', '#ffb86c', '#ff79c6',
        '#f8f8f2', '#6272a4', '#44475a', '#282a36', '#ff5555'
      ],
      'nord': [
        '#5e81ac', '#81a1c1', '#88c0d0', '#8fbcbb', '#d8dee9',
        '#bf616a', '#d08770', '#ebcb8b', '#a3be8c', '#b48ead'
      ],
      'monokai': [
        '#a6e22e', '#66d9ef', '#f92672', '#fd971f', '#ae81ff',
        '#e6db74', '#75715e', '#49483e', '#272822', '#f8f8f2'
      ],
      'gruvbox-dark': [
        '#fabd2f', '#83a598', '#8ec07c', '#fe8019', '#d3869b',
        '#fb4934', '#cc241d', '#98971a', '#d79921', '#458588'
      ],
      'one-dark': [
        '#61afef', '#98c379', '#e06c75', '#d19a66', '#c678dd',
        '#56b6c2', '#e5c07b', '#abb2bf', '#5c6370', '#282c34'
      ],
      'tokyo-night': [
        '#7aa2f7', '#9ece6a', '#f7768e', '#ff9e64', '#bb9af7',
        '#7dcfff', '#e0af68', '#c0caf5', '#565f89', '#1a1b26'
      ],
      'catppuccin-mocha': [
        '#cba6f7', '#94e2d5', '#f38ba8', '#fab387', '#a6e3a1',
        '#89b4fa', '#f9e2af', '#cdd6f4', '#6c7086', '#1e1e2e'
      ],
      'material-darker': [
        '#80cbc4', '#c3e88d', '#f07178', '#ffcb6b', '#c792ea',
        '#89ddff', '#82aaff', '#eeffff', '#616161', '#212121'
      ],
      'synthwave': [
        '#ff7edb', '#36f9f6', '#fede5d', '#fe4450', '#72f1b8',
        '#f97e72', '#ff8b39', '#f8f8f2', '#6d5a7a', '#2a2139'
      ],
      'ayu-dark': [
        '#ffb454', '#39bae6', '#f07178', '#95e6cb', '#d5ff80',
        '#ff8f40', '#ffee99', '#b3b1ad', '#4d5566', '#0a0e14'
      ],
      'palenight': [
        '#82aaff', '#c3e88d', '#f07178', '#ffcb6b', '#c792ea',
        '#89ddff', '#f78c6c', '#a6accd', '#676e95', '#292d3e'
      ],
      'oceanic-next': [
        '#6699cc', '#99c794', '#ec5f67', '#fac863', '#c594c5',
        '#5fb3b3', '#f99157', '#c0c5ce', '#65737e', '#1b2b34'
      ],
      'spacegray': [
        '#fac863', '#a6e22e', '#f92672', '#fd971f', '#ae81ff',
        '#66d9ef', '#e6db74', '#f8f8f2', '#747369', '#2c2c2c'
      ],
      'tomorrow-night': [
        '#81a2be', '#b5bd68', '#cc6666', '#f0c674', '#b294bb',
        '#8abeb7', '#de935f', '#c5c8c6', '#969896', '#1d1f21'
      ],
      'cobalt2': [
        '#ffc600', '#a5ff90', '#ff0088', '#ff9d00', '#9effff',
        '#80fcff', '#ffee80', '#ffffff', '#1e4e68', '#193549'
      ],
      'night-owl': [
        '#c792ea', '#addb67', '#ef5350', '#ffcb8b', '#82aaff',
        '#7fdbca', '#f78c6c', '#d6deeb', '#5f7e97', '#011627'
      ],
      'dark-plus': [
        '#569cd6', '#608b4e', '#f44747', '#dcdcaa', '#c586c0',
        '#4ec9b0', '#ce9178', '#d4d4d4', '#6a6a6a', '#1e1e1e'
      ],
      'high-contrast-dark': [
        '#00ff00', '#ffff00', '#ff0000', '#00ffff', '#ff00ff',
        '#ffffff', '#808080', '#c0c0c0', '#404040', '#000000'
      ],
      'shades-of-purple': [
        '#fad000', '#a599e9', '#ec3a37', '#ff7edb', '#72f1b8',
        '#36f9f6', '#fede5d', '#a599e9', '#6943ff', '#2d2b55'
      ],
      'minimal-light': [
        '#2c3e50', '#34495e', '#7f8c8d', '#95a5a6', '#bdc3c7',
        '#e74c3c', '#e67e22', '#f39c12', '#27ae60', '#3498db'
      ],
      'paper': [
        '#8d6e63', '#a1887f', '#bcaaa4', '#d7ccc8', '#efebe9',
        '#f44336', '#ff9800', '#ffc107', '#4caf50', '#2196f3'
      ],
      'github-light': [
        '#0969da', '#1f883d', '#cf222e', '#fb8500', '#8250df',
        '#0550ae', '#218bff', '#24292f', '#656d76', '#ffffff'
      ],
      'gruvbox-light': [
        '#b57614', '#79740e', '#cc241d', '#d65d0e', '#b16286',
        '#427b58', '#af3a03', '#3c3836', '#928374', '#fbf1c7'
      ],
      'solarized-light': [
        '#268bd2', '#859900', '#dc322f', '#cb4b16', '#d33682',
        '#2aa198', '#b58900', '#657b83', '#93a1a1', '#fdf6e3'
      ],
      'one-light': [
        '#4078f2', '#50a14f', '#e45649', '#c18401', '#a626a4',
        '#0184bc', '#986801', '#383a42', '#a0a1a7', '#fafafa'
      ],
      'catppuccin-latte': [
        '#8839ef', '#40a02b', '#d20f39', '#fe640b', '#ea76cb',
        '#179299', '#df8e1d', '#4c4f69', '#6c6f85', '#eff1f5'
      ],
      'material-light': [
        '#00bcd4', '#4caf50', '#f44336', '#ff9800', '#9c27b0',
        '#2196f3', '#ffc107', '#263238', '#90a4ae', '#fafafa'
      ],
      'nord-light': [
        '#5e81ac', '#a3be8c', '#bf616a', '#d08770', '#b48ead',
        '#88c0d0', '#ebcb8b', '#2e3440', '#5e81ac', '#eceff4'
      ],
      'ayu-light': [
        '#ff6a00', '#86b300', '#f51818', '#f2ae49', '#a37acc',
        '#4cbf99', '#ff8f40', '#5c6166', '#8a9199', '#fafafa'
      ],
      'light-plus': [
        '#005cc5', '#22863a', '#d73a49', '#e36209', '#6f42c1',
        '#032f62', '#b31d28', '#24292e', '#6a737d', '#ffffff'
      ],
      'quiet-light': [
        '#4078f2', '#50a14f', '#e45649', '#c18401', '#a626a4',
        '#0184bc', '#986801', '#333333', '#8e8e8e', '#f5f5f5'
      ],
      'winter-is-coming-light': [
        '#0066cc', '#008000', '#cd3131', '#ff8c00', '#800080',
        '#008080', '#b8860b', '#3c3c3c', '#7c7c7c', '#ffffff'
      ],
      'atom-one-light': [
        '#4078f2', '#50a14f', '#e45649', '#c18401', '#a626a4',
        '#0184bc', '#986801', '#383a42', '#a0a1a7', '#fafafa'
      ],
      'tomorrow': [
        '#4271ae', '#718c00', '#c82829', '#eab700', '#8959a8',
        '#3e999f', '#f5871f', '#4d4d4c', '#8e908c', '#ffffff'
      ],
      'intellij-light': [
        '#0033b3', '#008000', '#ff0000', '#ff8000', '#800080',
        '#008080', '#808000', '#000000', '#787878', '#ffffff'
      ],
      'high-contrast-light': [
        '#0000ff', '#008000', '#ff0000', '#ff8000', '#800080',
        '#008080', '#808000', '#000000', '#666666', '#ffffff'
      ],
      'spring-light': [
        '#2d5016', '#4caf50', '#d32f2f', '#ff9800', '#9c27b0',
        '#00bcd4', '#ffc107', '#1a3409', '#7fb069', '#f8fff8'
      ],
      'summer-light': [
        '#e65100', '#ff9800', '#d32f2f', '#ffc107', '#9c27b0',
        '#00bcd4', '#4caf50', '#bf360c', '#ff9800', '#fffbf0'
      ],
      // Pantone Colors of the Year - Dark Themes
      'pantone-classic-blue-dark': [
        '#0f4c81', '#34568b', '#5a7bb0', '#7fa3d3', '#a4c8f0'
      ],
      'pantone-ultimate-gray-dark': [
        '#f5df4d', '#939597', '#6c6c6c', '#4a4a4a', '#2c2c2c'
      ],
      'pantone-very-peri-dark': [
        '#6667ab', '#8a8bc7', '#aeafe3', '#d2d3ff', '#f6f6ff'
      ],
      'pantone-viva-magenta-dark': [
        '#bb2649', '#d14d6b', '#e7748d', '#fd9baf', '#ffc2d1'
      ],
      'pantone-peach-fuzz-dark': [
        '#ffbe98', '#ffd4b8', '#ffe9d8', '#fff5f0', '#ffffff'
      ],
      'pantone-mocha-mousse-dark': [
        '#ad8b73', '#c4a58f', '#dbbfab', '#f2d9c7', '#f5f0eb'
      ],
      // Pantone Colors of the Year - Light Themes
      'pantone-classic-blue-light': [
        '#0f4c81', '#2d5a94', '#4b68a7', '#6976ba', '#8784cd'
      ],
      'pantone-ultimate-gray-light': [
        '#f5df4d', '#939597', '#6c6c6c', '#4a4a4a', '#2c2c2c'
      ],
      'pantone-very-peri-light': [
        '#6667ab', '#7b7cb5', '#9091bf', '#a5a6c9', '#babbd3'
      ],
      'pantone-viva-magenta-light': [
        '#bb2649', '#c73d5c', '#d3546f', '#df6b82', '#eb8295'
      ],
      'pantone-peach-fuzz-light': [
        '#ff8c42', '#ffbe98', '#ffd4b8', '#ffe9d8', '#fff5f0'
      ],
      'pantone-mocha-mousse-light': [
        '#8b4513', '#ad8b73', '#c4a58f', '#dbbfab', '#f2d9c7'
      ]
    };

    return colorPalettes[currentTheme.id as keyof typeof colorPalettes] || colorPalettes['serika-dark'];
  };

  // Load columns from backend
  const loadColumns = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await columnApi.getColumns();
      setColumns(response.columns);
    } catch (err) {
      console.error('Failed to load columns:', err);
      setError('Failed to load columns');
      // Fallback to default columns if backend fails
      setColumns([
        { id: 'todo', title: 'To Do', status: 'todo', color: '#646669', order: 0, is_protected: true, createdAt: '', updatedAt: '' },
        { id: 'in-progress', title: 'In Progress', status: 'in-progress', color: '#e2b714', order: 1, is_protected: false, createdAt: '', updatedAt: '' },
        { id: 'review', title: 'Review', status: 'review', color: '#ff8c42', order: 2, is_protected: false, createdAt: '', updatedAt: '' },
        { id: 'done', title: 'Done', status: 'done', color: '#4ade80', order: 3, is_protected: false, createdAt: '', updatedAt: '' },
        { id: 'blocked', title: 'Blocked', status: 'blocked', color: '#ca4754', order: 4, is_protected: false, createdAt: '', updatedAt: '' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadColumns();
  }, []);

  const getTasksByStatus = (status: string) => {
    return tasks.filter(task => task.status === status);
  };

  const toggleColumnVisibility = (columnId: string) => {
    const newHiddenColumns = new Set(hiddenColumns);
    if (newHiddenColumns.has(columnId)) {
      newHiddenColumns.delete(columnId);
    } else {
      newHiddenColumns.add(columnId);
    }
    setHiddenColumns(newHiddenColumns);
  };

  const startEditingColumn = (columnId: string, currentTitle: string) => {
    setEditingColumn(columnId);
    setEditingTitle(currentTitle);
  };

  const saveColumnTitle = async () => {
    if (editingColumn && editingTitle.trim()) {
      try {
        await columnApi.updateColumn(editingColumn, { title: editingTitle.trim() });
        await loadColumns(); // Reload columns
      } catch (err) {
        console.error('Failed to update column:', err);
        setError('Failed to update column');
      }
    }
    setEditingColumn(null);
    setEditingTitle('');
  };

  const cancelEditingColumn = () => {
    setEditingColumn(null);
    setEditingTitle('');
  };

  const addNewColumn = async () => {
    if (newColumnTitle.trim()) {
      try {
        // Utwórz czytelny status na podstawie nazwy kolumny
        let cleanStatus = newColumnTitle.trim()
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, '') // Usuń znaki specjalne
          .replace(/\s+/g, '-') // Zamień spacje na myślniki
          .substring(0, 50); // Ogranicz długość
        
        // Sprawdź czy status już istnieje i dodaj suffix jeśli potrzeba
        const existingStatuses = columns.map(col => col.status);
        let counter = 1;
        let finalStatus = cleanStatus;
        
        while (existingStatuses.includes(finalStatus)) {
          finalStatus = `${cleanStatus}-${counter}`;
          counter++;
        }
        
        await columnApi.createColumn({
          title: newColumnTitle.trim(),
          status: finalStatus,
          color: newColumnColor
        });
        await loadColumns(); // Reload columns
        setNewColumnTitle('');
        setNewColumnColor(getThemeColors()[0]);
        setShowAddColumn(false);
      } catch (err) {
        console.error('Failed to create column:', err);
        setError('Failed to create column');
      }
    }
  };

  const deleteColumn = async (columnId: string) => {
    const columnToDelete = columns.find(col => col.id === columnId);
    if (!columnToDelete) return;

    // Check if column is protected
    if (columnToDelete.is_protected) {
      alert('Cannot delete this column. It is protected. You can rename it but not delete it.');
      return;
    }

    // Get tasks in the column to be deleted
    const tasksToMigrate = getTasksByStatus(columnToDelete.status);
    
    if (tasksToMigrate.length > 0) {
      // Find the first available column (excluding the one being deleted)
      const remainingColumns = columns.filter(col => col.id !== columnId);
      if (remainingColumns.length === 0) {
        alert('Cannot delete the last column!');
        return;
      }

      const targetColumn = remainingColumns[0];
      const confirmMessage = `This will delete the "${columnToDelete.title}" column and move ${tasksToMigrate.length} task(s) to "${targetColumn.title}". Continue?`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }

      // Migrate tasks to the first remaining column
      for (const task of tasksToMigrate) {
        await onStatusChange(task.id, targetColumn.status);
      }
    } else {
      if (!window.confirm(`Delete the "${columnToDelete.title}" column?`)) {
        return;
      }
    }

    try {
      await columnApi.deleteColumn(columnId);
      await loadColumns(); // Reload columns
      
      // Remove from hidden columns if it was hidden
      setHiddenColumns(prev => {
        const newSet = new Set(prev);
        newSet.delete(columnId);
        return newSet;
      });
    } catch (err: any) {
      console.error('Failed to delete column:', err);
      // Show specific error message from backend
      const errorMessage = err.response?.data?.error || 'Failed to delete column';
      setError(errorMessage);
    }
  };

  const updateColumnColor = async (columnId: string, color: string) => {
    try {
      await columnApi.updateColumn(columnId, { color });
      await loadColumns(); // Reload columns
      setShowColorPicker(null);
    } catch (err) {
      console.error('Failed to update column color:', err);
      setError('Failed to update column color');
    }
  };

  // Column drag and drop handlers
  const handleColumnDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnId);
  };

  const handleColumnDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleColumnDrop = async (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    const draggedColumnId = e.dataTransfer.getData('text/plain');
    
    if (draggedColumnId && draggedColumnId !== targetColumnId) {
      const draggedCol = columns.find(col => col.id === draggedColumnId);
      const targetCol = columns.find(col => col.id === targetColumnId);
      
      if (draggedCol && targetCol) {
        // Swap orders
        const newOrder = targetCol.order;
        const oldOrder = draggedCol.order;
        
        try {
          await columnApi.updateColumnOrder([
            { id: draggedColumnId, order: newOrder },
            { id: targetColumnId, order: oldOrder }
          ]);
          await loadColumns(); // Reload columns
        } catch (err) {
          console.error('Failed to reorder columns:', err);
          setError('Failed to reorder columns');
        }
      }
    }
    
    setDraggedColumn(null);
  };

  const handleColumnDragEnd = () => {
    setDraggedColumn(null);
  };

  // Task drag and drop handlers
  const handleTaskDragStart = (e: React.DragEvent, task: Task) => {
    e.dataTransfer.setData('text/plain', task.id);
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation(); // Prevent column drag
  };

  const handleTaskDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleTaskDrop = (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain');
    if (taskId) {
      onStatusChange(taskId, newStatus);
    }
  };

  const getColumnStats = (status: string) => {
    const columnTasks = getTasksByStatus(status);
    return {
      count: columnTasks.length,
      highPriority: columnTasks.filter(task => task.priority === 'high' || task.priority === 'critical').length
    };
  };

  const visibleColumns = columns.filter(column => !hiddenColumns.has(column.id));
  const themeColors = getThemeColors();

  // Set default color based on theme
  useEffect(() => {
    setNewColumnColor(themeColors[0]);
  }, [currentTheme]);

  if (loading) {
    return (
      <div className="kanban-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <span>Loading columns...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="kanban-container">
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      <div className="kanban-controls">
        <button 
          className={`compact-toggle ${isCompact ? 'active' : ''}`}
          onClick={() => setIsCompact(!isCompact)}
          title="Toggle compact view"
        >
          {isCompact ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          <span>{isCompact ? 'Expand' : 'Compact'}</span>
        </button>
        
        <div className="column-toggles">
          {columns.map(column => {
            const isHidden = hiddenColumns.has(column.id);
            const stats = getColumnStats(column.status);
            
            return (
              <button
                key={column.id}
                className={`column-toggle ${isHidden ? 'hidden' : 'visible'}`}
                onClick={() => toggleColumnVisibility(column.id)}
                title={`${isHidden ? 'Show' : 'Hide'} ${column.title} column`}
              >
                {isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
                <span>{column.title}</span>
                <span className="column-count">{stats.count}</span>
              </button>
            );
          })}
          
          <button
            className="add-column-btn"
            onClick={() => setShowAddColumn(true)}
            title="Add new column"
          >
            <Plus size={14} />
            <span>Add Column</span>
          </button>
        </div>
      </div>

      {showAddColumn && (
        <div className="add-column-modal">
          <div className="add-column-content">
            <h3>Add New Column</h3>
            <input
              type="text"
              value={newColumnTitle}
              onChange={(e) => setNewColumnTitle(e.target.value)}
              placeholder="Column title"
              onKeyDown={(e) => {
                if (e.key === 'Enter') addNewColumn();
                if (e.key === 'Escape') {
                  setShowAddColumn(false);
                  setNewColumnTitle('');
                  setNewColumnColor(themeColors[0]);
                }
              }}
              autoFocus
            />
            
            <div className="color-picker-section">
              <label>Column Color:</label>
              <div className="color-picker-container">
                <div 
                  className="color-preview"
                  style={{ backgroundColor: newColumnColor }}
                  onClick={() => setShowColorPicker('new')}
                />
                <input
                  type="color"
                  value={newColumnColor}
                  onChange={(e) => setNewColumnColor(e.target.value)}
                  className="color-input"
                />
              </div>
              
              {showColorPicker === 'new' && (
                <div className="predefined-colors">
                  <div className="color-palette-label">Theme Colors:</div>
                  {themeColors.map((color, index) => (
                    <div
                      key={`theme-${index}`}
                      className="color-option"
                      style={{ backgroundColor: color }}
                      onClick={() => {
                        setNewColumnColor(color);
                        setShowColorPicker(null);
                      }}
                      title={`Theme color ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
            
            <div className="add-column-actions">
              <button onClick={addNewColumn} disabled={!newColumnTitle.trim()}>
                <Check size={16} />
                Add
              </button>
              <button onClick={() => {
                setShowAddColumn(false);
                setNewColumnTitle('');
                setNewColumnColor(themeColors[0]);
              }}>
                <X size={16} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`kanban-board ${isCompact ? 'compact' : ''}`}>
        {visibleColumns.map((column) => {
          const columnTasks = getTasksByStatus(column.status);
          const stats = getColumnStats(column.status);
          const isDragging = draggedColumn === column.id;
          
          return (
            <div 
              key={column.id} 
              className={`kanban-column kanban-column-${column.status} ${isDragging ? 'dragging' : ''}`}
              style={{ borderLeftColor: column.color }}
              draggable
              onDragStart={(e) => handleColumnDragStart(e, column.id)}
              onDragOver={handleColumnDragOver}
              onDrop={(e) => handleColumnDrop(e, column.id)}
              onDragEnd={handleColumnDragEnd}
            >
              <div className="kanban-column-header">
                <div className="column-drag-handle">
                  <GripVertical size={16} className="drag-icon" />
                </div>
                
                <div className="kanban-column-title-container">
                  {editingColumn === column.id ? (
                    <div className="column-title-edit">
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveColumnTitle();
                          if (e.key === 'Escape') cancelEditingColumn();
                        }}
                        onBlur={saveColumnTitle}
                        autoFocus
                      />
                      <div className="column-edit-actions">
                        <button onClick={saveColumnTitle} title="Save">
                          <Check size={14} />
                        </button>
                        <button onClick={cancelEditingColumn} title="Cancel">
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="column-title-display">
                      <h3 className="kanban-column-title" style={{ color: column.color }}>
                        {column.title}
                      </h3>
                      <div className="column-actions">
                        <button
                          className="column-action-btn"
                          onClick={() => setShowColorPicker(showColorPicker === column.id ? null : column.id)}
                          title="Change column color"
                        >
                          <Palette size={14} />
                        </button>
                        <button
                          className="column-action-btn"
                          onClick={() => startEditingColumn(column.id, column.title)}
                          title="Edit column name"
                        >
                          <Edit2 size={14} />
                        </button>
                        {columns.length > 1 && !column.is_protected && (
                          <button
                            className="column-action-btn delete-btn"
                            onClick={() => deleteColumn(column.id)}
                            title="Delete column"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {showColorPicker === column.id && (
                    <div className="color-picker-dropdown">
                      <div className="predefined-colors">
                        <div className="color-palette-label">Theme Colors:</div>
                        {themeColors.map((color, index) => (
                          <div
                            key={`theme-${index}`}
                            className="color-option"
                            style={{ backgroundColor: color }}
                            onClick={() => updateColumnColor(column.id, color)}
                            title={`Theme color ${index + 1}`}
                          />
                        ))}
                      </div>
                      <input
                        type="color"
                        value={column.color}
                        onChange={(e) => updateColumnColor(column.id, e.target.value)}
                        className="color-input-full"
                      />
                    </div>
                  )}
                </div>
                
                <div className="kanban-column-stats">
                  <span className="task-count">{stats.count}</span>
                  {stats.highPriority > 0 && (
                    <span className="high-priority-count" title="High/Critical Priority">
                      {stats.highPriority}
                    </span>
                  )}
                </div>
              </div>
              
              <div 
                className="kanban-column-content"
                onDragOver={handleTaskDragOver}
                onDrop={(e) => handleTaskDrop(e, column.status)}
              >
                {columnTasks.length === 0 ? (
                  <div className="kanban-empty-state">
                    <p>No tasks</p>
                  </div>
                ) : (
                  columnTasks.map((task) => (
                    <div
                      key={task.id}
                      className="kanban-task-wrapper"
                      draggable
                      onDragStart={(e) => handleTaskDragStart(e, task)}
                    >
                      <TaskCard
                        task={task}
                        columns={columns}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onQuickStatusChange={onStatusChange}
                        isKanbanView={true}
                        onImagePreview={onImagePreview}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KanbanBoard; 